
import { NavLink, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FederatedModule } from '@bfme-technology/spa';
import { getStoredOverrides, checkDeveloperMode } from '../../../DeveloperWorkbench/DeveloperWorkbench.utils';



import type { SandboxProps } from './Sandbox.types';

export const Sandbox: React.FC<SandboxProps> = ({ routesLoading, routes }) => {
  const { t } = useTranslation();

  const getResolvedMfeProps = (route: any) => {
    const overrides = getStoredOverrides();
    const override = overrides[route.key];

    if (route.key === 'react-dashboard-mfe') {
      if (override) {
        return {
          mfeType: 'federated' as const,
          scope: 'remote_app',
          module: './Dashboard',
          scriptUrl: override.scriptUrl,
          cssUrl: override.cssUrl !== undefined ? override.cssUrl : route.cssUrl,
          mfeProps: {},
        };
      } else {
        return {
          mfeType: 'custom-element' as const,
          elementName: 'div',
          mfeProps: {},
          loadingFallback: (
            <div className="border-2 border-dashed border-purple-500/50 p-6 rounded-2xl bg-purple-500/5">
              <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
                <i className="fa-brands fa-react"></i>
                React Federated MFE
              </h4>
              <p className="text-slate-300 text-sm leading-relaxed">
                Federated components require container assembly. Under production building, this maps to the `@originjs/vite-plugin-federation` import hook.
              </p>
              <div className="mt-3 p-2.5 bg-white/3 rounded-lg text-xs font-mono text-slate-400">
                remoteEntry: http://localhost:5002/assets/remoteEntry.js
              </div>
            </div>
          ),
        };
      }
    }

    if (override) {
      return {
        mfeType: route.mfeType,
        elementName: route.elementName,
        bootstrapFnName: route.bootstrapFnName,
        unmountFnName: route.unmountFnName,
        scope: route.scope,
        module: route.module,
        scriptUrl: override.scriptUrl || route.scriptUrl,
        cssUrl: override.cssUrl !== undefined ? override.cssUrl : route.cssUrl,
        mfeProps: route.mfeProps || {},
      };
    }

    return route;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Controls */}
      <section className="glass-panel p-6">
        <h3 className="mb-4 text-base font-bold text-text-primary">{t('mfeSandbox')}</h3>
        <p className="text-xs md:text-sm text-text-secondary mb-5 leading-relaxed">
          Simulate loading independent applications built in different frameworks. Our dynamic wrapper injects scripts and renders them natively.
        </p>
        
        {routesLoading ? (
          <div className="text-text-muted text-xs">Loading routes configuration...</div>
        ) : (
          <div className="flex flex-col gap-3">
            {routes
              .filter((route) => route.path.startsWith('/sandbox/'))
              .map((route) => (
                <NavLink
                  key={route.key}
                  to={checkDeveloperMode() ? `${route.path}?developer=1` : route.path}
                  className={({ isActive }) => 
                    `block w-full p-4 rounded-2xl border transition-all duration-300 text-left text-sm ${
                      isActive 
                        ? 'border-primary-accent bg-primary-accent-bg text-primary-accent shadow-[0_0_15px_var(--border-glow)] font-semibold' 
                        : 'border-border-color bg-bg-surface/40 text-text-secondary hover:text-text-primary hover:border-border-color/80'
                    }`
                  }
                >
                  <i className="fa-solid fa-plug mr-2"></i> {route.name}
                </NavLink>
              ))}
          </div>
        )}
      </section>

      {/* MFE Mounting Sandbox Panel */}
      <section className="glass-panel p-6 lg:col-span-2 min-h-[320px] flex flex-col">
        <h3 className="mb-4 text-sm md:text-base font-bold text-primary-accent">{t('activeMfe')}</h3>
        
        <div className="grow flex items-center justify-center bg-bg-deep rounded-2xl border border-border-color p-4 md:p-6">
          <Routes>
            {/* Default nested route */}
            <Route path="/" element={
              <div className="self-center text-center text-text-muted">
                <span className="text-5xl block mb-4 text-text-muted/60">
                  <i className="fa-solid fa-plug animate-pulse"></i>
                </span>
                No micro-frontend selected. Click one of the buttons on the left to bootstrap.
              </div>
            } />
            
            {/* Dynamic nested routes */}
            {routes
              .filter((route) => route.path.startsWith('/sandbox/'))
              .map((route) => {
                const resolved = getResolvedMfeProps(route);
                const { key, ...mfeProps } = resolved;
                const relativePath = route.path.replace('/sandbox/', '');
                return (
                  <Route
                    key={route.key}
                    path={relativePath}
                    element={
                      <div className="w-full">
                        <FederatedModule {...mfeProps} />
                      </div>
                    }
                  />
                );
              })}
          </Routes>
        </div>
      </section>
    </div>
  );
};

export default Sandbox;
